import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageMeta } from '@/lib/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Unsubscribe() {
  usePageMeta({
    title: 'Unsubscribed | The Founder’s List',
    description: 'You have been removed from The Founder’s List.',
    path: '/unsubscribe',
    robots: 'noindex, nofollow',
  });
  const [params] = useSearchParams();
  const state = params.get('state') || 'success';
  const email = params.get('email') || '';
  const token = params.get('token') || '';
  const [rejoining, setRejoining] = useState(false);
  const [rejoined, setRejoined] = useState(false);

  useEffect(() => {
    // If the user landed directly with ?token=xxx (e.g. via an <a href> without server redirect),
    // hit the unsubscribe endpoint on their behalf.
    if (token && state !== 'success' && state !== 'invalid') {
      window.location.replace(`${API}/newsletter/unsubscribe?token=${encodeURIComponent(token)}`);
    }
  }, [token, state]);

  const rejoin = async () => {
    if (!token) {
      toast.error('Missing unsubscribe token. Please rejoin from the homepage.');
      return;
    }
    setRejoining(true);
    try {
      await axios.post(`${API}/newsletter/resubscribe?token=${encodeURIComponent(token)}`);
      setRejoined(true);
      toast.success('Welcome back to The Founder’s List.');
    } catch (e) {
      toast.error('Something went wrong. Please try again from the homepage.');
    } finally {
      setRejoining(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen pt-40 pb-32" data-testid="unsubscribe-page">
      <div className="mx-auto max-w-[680px] px-6 sm:px-10 text-center">
        <div className="eyebrow text-taupe mb-8">The Founder’s List</div>
        {state === 'invalid' ? (
          <>
            <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.1] tracking-tight text-matte">
              We couldn’t process that link.
            </h1>
            <p className="mt-6 text-matte/70 leading-relaxed max-w-md mx-auto">
              This unsubscribe link is missing or has already been used. If you’d like to leave the list, please
              write to <a className="underline hover:text-gold" href="mailto:hello@iamtoiawallace.com">hello@iamtoiawallace.com</a>.
            </p>
          </>
        ) : rejoined ? (
          <>
            <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.1] tracking-tight text-matte">
              Welcome back.
            </h1>
            <p className="mt-6 text-matte/70 leading-relaxed">
              You’re on The Founder’s List again. The next Founder’s Brief lands this Friday.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.1] tracking-tight text-matte">
              You’ve been removed from The Founder’s List.
            </h1>
            <p className="mt-8 text-matte/70 leading-relaxed max-w-md mx-auto">
              You will no longer receive The Founder’s Brief. You’re always welcome to join us again.
            </p>
            {email && (
              <p className="mt-3 text-matte/45 text-[12px]">Removed: {email}</p>
            )}
            <button
              type="button"
              onClick={rejoin}
              disabled={rejoining}
              data-testid="unsubscribe-rejoin"
              className="mt-12 btn-primary disabled:opacity-60"
            >
              {rejoining ? 'Rejoining…' : 'Rejoin The Founder’s List'}
            </button>
          </>
        )}
        <div className="mt-12">
          <Link to="/" className="btn-ghost">Return home</Link>
        </div>
      </div>
    </div>
  );
}
